import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  InternalApiRateLimitBucket,
  InternalApiRateLimitConfiguration,
  InternalApiRateLimitEndpoint,
} from '../../api-internal';
import { getRateLimitConfig, updateRateLimitBucket } from './ratelimitActions';
import RateLimiterTable from './RateLimiterTable';
import { Col } from 'react-bootstrap';
import { internalRateLimitBucketToRateLimitBucket } from './helper';
import './RateLimiterConfig.scss';
import { SalvButton, Size } from '@salvapp/ui-components';
import RateLimiterGroupEditModal, { RateLimiterGroupModalState } from './RateLimiterGroupEditModal';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import SalvPage from '../../common/SalvPage';

export type RateLimitBucket = InternalApiRateLimitBucket & { id: string };

type RateLimitConfiguration = Pick<
  InternalApiRateLimitConfiguration,
  'remainingLimitPerSecond' | 'availableEndpoints'
> & { buckets: RateLimitBucket[] };

const RateLimiterConfig = () => {
  const [rateLimitConfig, setRateLimitConfig] = useState<RateLimitConfiguration>();
  const [isNewGroupModalOpen, setIsNewGroupModalOpen] = useState(false);

  const fetchRateLimitConfig = useCallback(() => {
    getRateLimitConfig().then((response) => {
      setRateLimitConfig({
        buckets: response.buckets.map(internalRateLimitBucketToRateLimitBucket),
        availableEndpoints: response.availableEndpoints,
        remainingLimitPerSecond: response.remainingLimitPerSecond,
      });
    });
  }, []);

  useEffect(() => {
    fetchRateLimitConfig();
  }, [fetchRateLimitConfig]);

  const defaultBucket: RateLimitBucket = useMemo(() => {
    const endPointNamesInGroups =
      rateLimitConfig?.buckets.flatMap((it) => it.endpoints.map((it) => it.endpointName)) || [];
    const availableEndPoints =
      rateLimitConfig?.availableEndpoints?.filter((it) => !endPointNamesInGroups.includes(it.endpointName)) || [];

    return {
      id: 'All_endpoints',
      bucketName: 'All endpoints',
      endpoints: availableEndPoints,
      limitPerSecond: rateLimitConfig?.remainingLimitPerSecond || -1,
    };
  }, [rateLimitConfig]);

  const allBuckets = useMemo(() => {
    return [
      defaultBucket,
      ...(rateLimitConfig?.buckets?.sort((a, b) => a.bucketName.localeCompare(b.bucketName)) || []),
    ];
  }, [defaultBucket, rateLimitConfig]);

  const moveEndPoints = async (
    originalBucket: RateLimitBucket,
    newBucket: RateLimitBucket,
    paths: InternalApiRateLimitEndpoint[]
  ) => {
    if (originalBucket.id !== defaultBucket.id) {
      const names = paths?.map((it) => it.endpointName) || [];
      const endpoints = originalBucket?.endpoints?.filter((it) => !names.includes(it.endpointName)) || [];
      await updateRateLimitBucket({ ...originalBucket, endpoints });
    }
    if (newBucket.id !== defaultBucket.id) {
      const newEndPoints = paths.concat(newBucket?.endpoints || []);
      await updateRateLimitBucket({ ...newBucket, endpoints: newEndPoints });
    }
  };

  const createNewGroup = useCallback(
    async (bucketName: string, limitPerSecond: number) => {
      await updateRateLimitBucket({
        bucketName: bucketName,
        endpoints: [] as InternalApiRateLimitEndpoint[],
        limitPerSecond: limitPerSecond,
      } as RateLimitBucket);
      setIsNewGroupModalOpen(false);
      fetchRateLimitConfig();
    },
    [fetchRateLimitConfig]
  );

  return (
    <SalvPage
      title='Rate limiter'
      actions={
        <SalvButton
          label='New group'
          className='m-r-0'
          size={Size.SMALL}
          icon={faPlus}
          onClick={() => setIsNewGroupModalOpen(true)}
        />
      }
    >
      <div className='d-flex'>
        <Col sm={6} className='rate-limiter-default-group p-l-0 p-r-32'>
          <RateLimiterTable
            bucket={defaultBucket}
            isDefaultGroup={true}
            allBuckets={allBuckets}
            moveEndPoints={moveEndPoints}
            refresh={fetchRateLimitConfig}
          />
        </Col>
        <Col sm={6} className='rate-limiter-other-groups p-l-32 p-r-0'>
          {rateLimitConfig?.buckets?.map((bucket) => (
            <RateLimiterTable
              key={bucket.id}
              bucket={bucket}
              isDefaultGroup={false}
              allBuckets={allBuckets}
              moveEndPoints={moveEndPoints}
              refresh={fetchRateLimitConfig}
            />
          ))}
        </Col>
      </div>
      {isNewGroupModalOpen && (
        <RateLimiterGroupEditModal
          setShow={setIsNewGroupModalOpen}
          state={RateLimiterGroupModalState.CREATE_NEW_GROUP}
          onSubmit={createNewGroup}
          otherBucketNames={allBuckets.map((it) => it.bucketName)}
        />
      )}
    </SalvPage>
  );
};
export default RateLimiterConfig;
